/**
 * Shared utility functions for DPWeb SPAs.
 */

const SNOWFLAKE_EPOCH = new Date('2026-09-01T00:00:00Z').getTime()
const D_EPOCH = new Date('2025-12-31T00:00:00Z').getTime()

/**
 * Convert a snowflake ID (i column) to a JavaScript Date.
 * The ID encodes milliseconds since SNOWFLAKE_EPOCH.
 */
export function snowflakeToDate(id: number | undefined | null): Date | null {
  if (!id) return null
  const ms = Math.floor(id / 10000)
  return new Date(SNOWFLAKE_EPOCH + ms)
}

/**
 * Convert a snowflake ID to a formatted date string.
 * Returns empty string for falsy input.
 */
export function formatDateFromId(id: number | undefined | null): string {
  const d = snowflakeToDate(id)
  if (!d) return ''
  return d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })
}

/**
 * Convert a now_d() column value (d column) to a JavaScript Date.
 * The value encodes milliseconds since D_EPOCH.
 */
export function dToDate(d: number | undefined | null): Date | null {
  if (!d) return null
  return new Date(D_EPOCH + d)
}

/**
 * Convert a now_d() column value to a formatted date string.
 */
export function formatDateFromD(d: number | undefined | null): string {
  const date = dToDate(d)
  if (!date) return ''
  return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })
}

// ── Phase 2: currency / timezone helpers ──────────────────────────
// Prices are stored in the store's configured display currency label.
// Stock dates are Unix
// epoch milliseconds; display uses the store's compact UTC offset.

export function formatPrice(
  amount: number | string | undefined | null,
  currency = 'MMK',
): string {
  if (amount === undefined || amount === null || amount === '') return ''
  const n = typeof amount === 'string' ? Number(amount) : amount
  if (!Number.isFinite(n)) return ''
  try {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(n)
  } catch {
    return `${currency} ${n.toFixed(2)}`
  }
}

export function formatStockDate(
  epochMs: number | undefined | null,
  timezone = '0',
): string {
  if (epochMs === undefined || epochMs === null) return ''

  const offsetMinutes = parseCompactUtcOffset(timezone)
  if (offsetMinutes === null) {
    return new Date(epochMs).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    })
  }
  try {
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      timeZone: 'UTC',
    }).format(new Date(epochMs + offsetMinutes * 60_000))
  } catch {
    return new Date(epochMs).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    })
  }
}

function parseCompactUtcOffset(timezone: string): number | null {
  const match = /^([+-]?)(\d{1,3})$/.exec(timezone.trim())
  if (!match) return null

  const [, sign, digits] = match
  const hours = Number(digits.length <= 2 ? digits : digits.slice(0, -2))
  const minutes = Number(digits.length <= 2 ? 0 : digits.slice(-2))
  if (hours > 14 || minutes > 59 || (hours === 14 && minutes !== 0)) return null

  const magnitude = hours * 60 + minutes
  return sign === '-' ? -magnitude : magnitude
}

export const STOCK_STATUS_LABEL: Record<number, string> = {
  [-1]: 'Error',
  0: 'Available',
  1: 'Booked',
  2: 'Sold',
  3: 'Delivered',
}

export function stockStatusLabel(s: number | undefined | null): string {
  if (s === undefined || s === null) return ''
  return STOCK_STATUS_LABEL[s] ?? String(s)
}
